import { useCallback, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { BaseRequest } from "../../utils/Classes/Requests/BaseRequest";
import useMerchantPortalAxios from "../../utils/axios";
import {
  setError,
  setFeatureData,
  setTableData,
  setTableLoading,
} from "../store/slices/appSlice";

function getSessionId() {
  return sessionStorage.getItem("SessionID");
}

function buildExchangeRateRequest() {
  return new BaseRequest(getSessionId());
}

function buildExchangeHistoryRequest(type, startDate, endDate) {
  return {
    SessionID: getSessionId(),
    ExchangeType: type,
    DateFrom: startDate,
    DateTo: endDate,
  };
}

export default function useNewExchangeAPI() {
  const dispatch = useDispatch();
  const client = useMerchantPortalAxios();
  const clientRef = useRef(client);
  const [exchangeRate, setExchangeRate] = useState(null);
  const [exchangeHistory, setExchangeHistory] = useState(null);
  const [exchangeHistoryLoading, setExchangeHistoryLoading] = useState(true);
  const [totalNbPage, setTotalNbPage] = useState(0);

  clientRef.current = client;

  const getExchangeRate = useCallback(async () => {
    const response = await clientRef.current.post(
      "/v1/Exchange/GetExchangeRate",
      buildExchangeRateRequest(),
    );

    const payload = response?.data?.Payload || null;
    setExchangeRate(payload);
    dispatch(
      setFeatureData({
        section: "summary",
        key: "exchange",
        value: payload || {},
      }),
    );
    return payload;
  }, [dispatch]);

  const getExchangeHistory = useCallback(
    async (type, startDate, endDate) => {
      setExchangeHistoryLoading(true);
      dispatch(setTableLoading({ key: "exchange", loading: true }));
      dispatch(setError(null));
      try {
        const response = await clientRef.current.post(
          "/v1/Exchange/GetExchangeHistory",
          buildExchangeHistoryRequest(type, startDate, endDate),
        );

        const payload = response?.data?.Payload || {};
        const historyList = payload.ExchangeHistoryList || [];

        setExchangeHistory(historyList);
        setTotalNbPage(historyList.length);
        dispatch(
          setTableData({
            key: "exchange",
            data: historyList,
            pagination: {
              page: 1,
              totalPages: historyList.length ? historyList[0]?.TotalPages || 0 : 0,
              totalCount: historyList.length ? historyList[0]?.TotalCount || 0 : 0,
            },
          }),
        );
        dispatch(
          setFeatureData({
            section: "forms",
            key: "exchange",
            value: {
              exchangeType: type,
              dateFrom: startDate,
              dateTo: endDate,
            },
          }),
        );

        return historyList;
      } catch (error) {
        dispatch(
          setTableData({
            key: "exchange",
            data: [],
            pagination: { page: 1, totalPages: 0, totalCount: 0 },
          }),
        );
        dispatch(
          setError(
            error?.response?.data?.Message ||
              error?.response?.data?.message ||
              "Unable to load exchange history.",
          ),
        );
        throw error;
      } finally {
        setExchangeHistoryLoading(false);
        dispatch(setTableLoading({ key: "exchange", loading: false }));
      }
    },
    [dispatch],
  );

  const InitiateExchange = useCallback(async (payload) => {
    const response = await clientRef.current.post(
      "/v1/Exchange/InitiateExchange",
      payload,
    );

    return response?.data?.Payload;
  }, []);

  return {
    getExchangeHistory,
    getExchangeRate,
    InitiateExchange,
    exchangeRate,
    exchangeHistory,
    exchangeHistoryLoading,
    totalNbPage,
  };
}
